import { html, nothing, type TemplateResult } from "lit";
import { repeat } from "lit/directives/repeat.js";
import type { Block, ChatLabels, Inline, SourceReference } from "./types.js";

export function renderBlocks(
  blocks: Block[],
  sources: SourceReference[],
  labels: ChatLabels | null,
): TemplateResult {
  return html`${repeat(
    blocks,
    (_block, index) => index,
    (block, index) => renderBlock(block, index, sources, labels),
  )}`;
}

function renderBlock(
  block: Block,
  blockIndex: number,
  sources: SourceReference[],
  labels: ChatLabels | null,
): TemplateResult {
  switch (block.type) {
    case "paragraph":
      return html`<p>${renderInlines(block.inlines, sources, labels)}</p>`;
    case "heading":
      return renderHeading(block, sources, labels);
    case "bulletList":
      return html`<ul>
        ${repeat(
          block.items,
          (_item, index) => index,
          (item) =>
            html`<li class="jonas-line jonas-line--list">
              ${renderBlocks(item.blocks, sources, labels)}
            </li>`,
        )}
      </ul>`;
    case "orderedList":
      return html`<ol start=${block.start}>
        ${repeat(
          block.items,
          (_item, index) => index,
          (item) =>
            html`<li class="jonas-line jonas-line--list">
              ${renderBlocks(item.blocks, sources, labels)}
            </li>`,
        )}
      </ol>`;
    case "blockquote":
      return html`<blockquote>
        ${renderBlocks(block.blocks, sources, labels)}
      </blockquote>`;
    case "codeBlock":
      return html`<pre><code
          class=${block.language ? `language-${block.language}` : nothing}
          >${block.text}</code></pre>`;
    case "table":
      return renderTable(block, blockIndex, sources, labels);
    case "thematicBreak":
      return html`<hr />`;
    default:
      return html``;
  }
}

function renderHeading(
  block: Extract<Block, { type: "heading" }>,
  sources: SourceReference[],
  labels: ChatLabels | null,
): TemplateResult {
  const children = renderInlines(block.inlines, sources, labels);
  switch (block.level) {
    case 1:
      return html`<h1>${children}</h1>`;
    case 2:
      return html`<h2>${children}</h2>`;
    case 3:
      return html`<h3>${children}</h3>`;
    case 4:
      return html`<h4>${children}</h4>`;
    case 5:
      return html`<h5>${children}</h5>`;
    default:
      return html`<h6>${children}</h6>`;
  }
}

function renderTable(
  block: Extract<Block, { type: "table" }>,
  blockIndex: number,
  sources: SourceReference[],
  labels: ChatLabels | null,
): TemplateResult {
  return html`<table>
    ${block.head
      ? html`<thead>
          <tr>
            ${block.head.cells.map(
              (cell, index) =>
                html`<th
                  style=${block.align[index]
                    ? `text-align:${block.align[index]}`
                    : nothing}
                >
                  ${renderInlines(cell.inlines, sources, labels)}
                </th>`,
            )}
          </tr>
        </thead>`
      : nothing}
    <tbody>
      ${repeat(
        block.rows,
        (_row, index) => index,
        (row, rowIndex) =>
          html`<tr>
            ${row.cells.map(
              (cell, index) =>
                html`<td
                  class="jonas-line jonas-line--cell"
                  data-flip-key=${`t${blockIndex}r${rowIndex}c${index}`}
                  style=${block.align[index]
                    ? `text-align:${block.align[index]}`
                    : nothing}
                >
                  <span class="jonas-cell"
                    >${renderInlines(cell.inlines, sources, labels)}</span
                  >
                </td>`,
            )}
          </tr>`,
      )}
    </tbody>
  </table>`;
}

function renderInlines(
  inlines: Inline[],
  sources: SourceReference[],
  labels: ChatLabels | null,
): TemplateResult {
  return html`${repeat(
    inlines,
    (inline, index) =>
      inline.type === "pill" ? `pill-${inline.ordinal}` : `n-${index}`,
    (inline) => renderInline(inline, sources, labels),
  )}`;
}

function renderInline(
  inline: Inline,
  sources: SourceReference[],
  labels: ChatLabels | null,
): TemplateResult {
  switch (inline.type) {
    case "text":
      return html`${inline.value}`;
    case "strong":
      return html`<strong
        >${renderInlines(inline.inlines, sources, labels)}</strong
      >`;
    case "emphasis":
      return html`<em>${renderInlines(inline.inlines, sources, labels)}</em>`;
    case "code":
      return html`<code>${inline.value}</code>`;
    case "link":
      return html`<a
        href=${inline.href}
        target="_blank"
        rel="noopener noreferrer"
        >${renderInlines(inline.inlines, sources, labels)}</a
      >`;
    case "lineBreak":
      return html`<br />`;
    case "pill":
      return renderPill(inline.ordinal, sources, labels);
    default:
      return html``;
  }
}

function renderPill(
  ordinal: number,
  sources: SourceReference[],
  labels: ChatLabels | null,
): TemplateResult {
  const source = sources[ordinal - 1];
  if (!source) return html``;
  const actionLabel = resolveSourceActionLabel(source, labels);
  const sourceFallback = labels?.sourceFallback?.trim() || "Bron";
  return html`<help-jonas-source-pill
    .label=${source.label}
    .url=${source.url}
    .sourceTitle=${source.title}
    .excerpt=${source.excerpt}
    .sourcetag=${source.sourcetag}
    .sourceFallback=${sourceFallback}
    .kind=${source.kind}
    .actionLabel=${actionLabel}
    .opensInNewTab=${source.opensInNewTab}
  ></help-jonas-source-pill>`;
}

function resolveSourceActionLabel(
  source: SourceReference,
  labels: ChatLabels | null,
): string {
  if (source.kind === "external") {
    return labels?.sourceOpenNewTab?.trim() || "Open in nieuw tabblad";
  }
  if (source.kind === "video") {
    return labels?.sourceOpenVideo?.trim() || "Open video";
  }
  return labels?.sourceOpen?.trim() || "Open bron";
}
